/*
 * TEMPORARY (CHI-20, John via Alpha SM, 2026-10-31): "Complete Your Ticket Purchase".
 *
 * Members who already hold a seat-holding registration (but no ticket) see this in place of
 * Buy Tickets in the EVENT PAGE ticket card (not the hero, out of scope), for any slug. It links straight to the /events/:slug/tickets cart (quantity -> attendees ->
 * Stripe). The server decides eligibility and folds in TICKETS_COMPLETE_PURCHASE_ENABLED;
 * the UI only reads viewerTicketing.completePurchaseEligible (missing = false).
 *
 * Precedence: paid (active tickets / finalizing order -> View My Ticket(s)) beats eligibility;
 * shown only where Buy Tickets would show, so ended events and the kill switch
 * (ticketsCheckoutEnabled off) hide it too.
 *
 * TO REMOVE: delete this file and server/chi20.completeTicketPurchase*.test.ts*, then remove the
 * imports/usages tagged "CompleteTicketPurchase (temporary)" in
 * EventDetail.tsx, pages/EventTickets.tsx, tickets/TicketPurchaseParts.tsx (lockEmail), tickets/EventTicketsPanel.tsx, hooks/useMyTicketsForEvent.ts and
 * lib/ticketingApi.ts (viewerTicketing field and the tickets_* error mapping may stay).
 */
import { Link } from "wouter";
import { Ticket } from "lucide-react";
import type { CSSProperties } from "react";
import { ticketsHref } from "@/lib/eventCta";

export const COMPLETE_TICKET_PURCHASE_LABEL = "Complete Your Ticket Purchase";

/** Dev2 contract: optional on every payload; a missing value means false. */
export type ViewerTicketing = { completePurchaseEligible?: boolean };

/** The ONE accessor. Source (SM contract): myTicketsForEvent only (useMyTicketsForEvent state). */
export function completePurchaseEligibleFrom(myTicketsForEvent: unknown): boolean {
  return (myTicketsForEvent as { viewerTicketing?: ViewerTicketing | null } | null | undefined)?.viewerTicketing?.completePurchaseEligible === true;
}

/** Replaces Buy Tickets only where Buy Tickets would show, and never for a paid buyer. */
export function shouldShowCompleteTicketPurchase(input: { showBuyTickets: boolean; eligible: boolean; hasTicketsToView: boolean }): boolean {
  return input.showBuyTickets && input.eligible && !input.hasTicketsToView;
}

export function CompleteTicketPurchaseButton({ slug, className = "chi-btn-primary", style }: { slug: string; className?: string; style?: CSSProperties }) {
  return (
    <Link
      href={ticketsHref(slug)}
      className={className}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", textDecoration: "none", ...style }}
      data-testid="complete-ticket-purchase"
    >
      <Ticket size={16} aria-hidden="true" /> {COMPLETE_TICKET_PURCHASE_LABEL}
    </Link>
  );
}

/**
 * Cart (/events/:slug/tickets) for an eligible member: row 1 locked to the account email, the
 * quantity picker first, and no already_registered / internal_checkout_required messaging.
 */
export function completePurchaseCart(input: { eligible: boolean; accountEmail?: string | null }) {
  const on = input.eligible;
  return {
    lockBuyerEmail: on && Boolean(input.accountEmail?.trim()),
    quantityFirst: on,
    hideMessage: (reason: string | null | undefined) => on && Boolean(reason) && /already[_ ]registered|internal[_ ]checkout[_ ]required/i.test(reason ?? ""),
  };
}
